import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DIAM_API_PATH,
  DIAM_PAGE_LIMIT,
  diamQueryKeys,
  getTeamApplicationContextsPage,
  getTeamRolesPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import { SelectOption } from '../CreateTeamDrawer/types';
import type { InviteRoleOption } from './types';

export function useInviteMemberOptions(
  teamId: string | undefined,
  isOpen: boolean,
) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);
  const enabled = isSignedIn && isOpen && Boolean(teamId);

  const applicationContextsQuery = useQuery({
    queryKey: diamQueryKeys.teamApplicationContexts(teamId ?? ''),
    queryFn: async () => {
      const idToken = await getIdToken();
      return getTeamApplicationContextsPage(apiUrl, idToken, teamId!, {
        page: 0,
        limit: DIAM_PAGE_LIMIT,
      });
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const rolesQuery = useQuery({
    queryKey: [...diamQueryKeys.teamRoles(teamId ?? '', {}), 'invite'] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      return getTeamRolesPage(apiUrl, idToken, teamId!, {
        page: 0,
        limit: DIAM_PAGE_LIMIT,
      });
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const applicationOptions = useMemo<SelectOption[]>(
    () =>
      (applicationContextsQuery.data?.elements ?? []).map(application => ({
        label: application.name,
        value: application.id,
      })),
    [applicationContextsQuery.data],
  );

  const roleOptions = useMemo<InviteRoleOption[]>(
    () =>
      (rolesQuery.data?.elements ?? [])
        .map(role => ({
          id: role.roleId,
          label: role.name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [rolesQuery.data?.elements],
  );

  return {
    applicationOptions,
    roleOptions,
    isLoadingApplications: applicationContextsQuery.isLoading,
    isLoadingRoles: rolesQuery.isLoading,
    isLoading: applicationContextsQuery.isLoading || rolesQuery.isLoading,
  };
}
